import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BellIcon, ClockIcon, PaperplaneIcon } from '@navikt/aksel-icons';
import { SurveyFormData } from '../SurveyFormCard';
import { SurveyFormCardUtils } from '../../../utils/SurveyFormCardUtils/SurveyFormCard.utils';
import { useGetDistributionChannelsQuery } from '../../../hooks';
import { GhostLoader } from '../../GhostLoader';
import { DateUtils } from '../../../utils/DateUtils';
import {
  WizardField,
  WizardFieldControlProps,
} from '../WizardField/WizardField';
import {
  addDays,
  formatDayMonth,
  getScheduledDate,
  normalizeTime,
  withCurrent,
} from './surveyTimeline.utils';
import classes from './SurveyTimeline.module.css';

type SurveyTimelineProps = {
  handleChange: (
    name: string,
    value: string | number | boolean | string[] | undefined,
  ) => void;
  formData: SurveyFormData;
  errors: Record<string, string> | undefined;
  /** An existing planned survey always has a send time, so "send now" is not offered. */
  requirePlanning?: boolean;
};

const DEADLINE_OPTIONS = [1, 2, 3, 5, 7, 14, 21, 30];
const REMINDER_OPTIONS = [1, 2, 3, 5, 7];
// The label is HH:mm in local time, the same format and time zone as formData.time
const TIME_SLOTS = DateUtils.generateTimeSlots(8, 17, 15).map((slot) => slot.label);

export const SurveyTimeline = ({
  handleChange,
  formData,
  errors,
  requirePlanning = false,
}: SurveyTimelineProps) => {
  const { t, i18n } = useTranslation();
  const {
    data: distributionChannels,
    isLoading,
    error,
  } = useGetDistributionChannelsQuery(formData.team);

  const hasChannels = !!SurveyFormCardUtils.hasDistributionChannel(distributionChannels);
  const canPlan = hasChannels || requirePlanning;
  const isPlanning = canPlan && formData.isPlanning;

  const clearPlanning = () => {
    handleChange('isPlanning', false);
    handleChange('date', '');
    handleChange('time', '');
    handleChange('isRepeating', false);
    handleChange('frequency', '');
  };

  // Planning needs a channel to send through. A restored draft may still say
  // "planned" after the team's last channel was removed.
  useEffect(() => {
    if (!isLoading && distributionChannels && !canPlan && formData.isPlanning) {
      clearPlanning();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, distributionChannels, canPlan, formData.isPlanning]);

  if (isLoading) {
    return <GhostLoader style={{ height: 240, width: '100%' }} />;
  }

  if (error) {
    return (
      <div className="ds-alert" data-color="danger" role="alert">
        <h2 className={`ds-heading ${classes.alertHeading}`} data-size="2xs">
          {t('surveyCreation.timeline.error')}
        </h2>
        <p className="ds-paragraph">{error.message}</p>
      </div>
    );
  }

  const locale = i18n.language === 'en' ? 'en-GB' : 'nb-NO';
  const time = normalizeTime(formData.time);
  const deadline = formData.deadlineInDays;
  const reminder = formData.daysBeforeDeadlineToNotify;
  const showActionDeadlines =
    hasChannels && formData.surveyType === 'work-environment';

  const days = (count: number) => t('surveyCreation.timeline.days', { count });

  const numberSelect = (
    name: keyof SurveyFormData,
    value: number | undefined,
    options: number[],
    label: (count: number) => string,
  ) =>
    (control: WizardFieldControlProps) => (
      <select
        {...control}
        className="ds-input"
        name={name}
        value={value ?? ''}
        onChange={(e) => handleChange(name, Number(e.target.value))}
      >
        {withCurrent(options, value).map((option) => (
          <option key={option} value={option}>
            {label(option)}
          </option>
        ))}
      </select>
    );

  const sendDate = isPlanning ? getScheduledDate(formData.date, time) : new Date();
  const deadlineDate = sendDate && addDays(sendDate, deadline);
  const reminderDate =
    deadlineDate && reminder > 0 ? addDays(deadlineDate, -reminder) : null;

  const timelineItems = sendDate
    ? [
        {
          key: 'send',
          icon: <PaperplaneIcon aria-hidden="true" />,
          what: t('surveyCreation.timeline.preview.send'),
          when: isPlanning
            ? t('surveyCreation.timeline.preview.at', {
                date: formatDayMonth(sendDate, locale),
                time,
              })
            : t('surveyCreation.timeline.preview.now'),
        },
        ...(reminderDate
          ? [
              {
                key: 'reminder',
                icon: <BellIcon aria-hidden="true" />,
                what: t('surveyCreation.timeline.preview.reminder'),
                when: isPlanning
                  ? formatDayMonth(reminderDate, locale)
                  : t('surveyCreation.timeline.preview.daysAfterSend', {
                      count: deadline - reminder,
                    }),
              },
            ]
          : []),
        {
          key: 'deadline',
          icon: <ClockIcon aria-hidden="true" />,
          what: t('surveyCreation.timeline.preview.deadline'),
          when: isPlanning
            ? formatDayMonth(deadlineDate!, locale)
            : t('surveyCreation.timeline.preview.daysAfterSend', { count: deadline }),
        },
      ]
    : [];

  const repeatOptions = [
    { value: '', label: t('surveyCreation.timeline.planning.noRepeat') },
    ...SurveyFormCardUtils.getFrequencyOptions(),
  ];
  const selectedRepeat = formData.isRepeating ? formData.frequency ?? '' : '';

  return (
    <div className={classes.container}>
      {canPlan && !requirePlanning && (
        <fieldset className={`ds-fieldset ${classes.fieldset}`}>
          <legend className="ds-label">
            {t('surveyCreation.timeline.sendQuestion')}
          </legend>
          <div className={classes.chips}>
            <label className="ds-chip">
              <input
                type="radio"
                name="send-mode"
                checked={!isPlanning}
                onChange={clearPlanning}
              />
              {t('surveyCreation.timeline.sendNow')}
            </label>
            <label className="ds-chip">
              <input
                type="radio"
                name="send-mode"
                checked={isPlanning}
                onChange={() => handleChange('isPlanning', true)}
              />
              {t('surveyCreation.timeline.sendLater')}
            </label>
          </div>
        </fieldset>
      )}

      {!canPlan && (
        <div className="ds-alert" data-color="info">
          <h2 className={`ds-heading ${classes.alertHeading}`} data-size="2xs">
            {t('surveyCreation.timeline.noChannels.title')}
          </h2>
          <p className="ds-paragraph">
            {t('surveyCreation.timeline.noChannels.description')}
          </p>
        </div>
      )}

      <div className={classes.grid}>
        {isPlanning && (
          <>
            <WizardField
              id="survey-date"
              label={t('surveyCreation.timeline.planning.date')}
              error={errors?.date}
            >
              {(control) => (
                <input
                  {...control}
                  className="ds-input"
                  type="date"
                  name="date"
                  min={requirePlanning ? undefined : new Date().toLocaleDateString('sv-SE')}
                  value={formData.date || ''}
                  onChange={(e) => handleChange('date', e.target.value)}
                />
              )}
            </WizardField>
            <WizardField
              id="survey-time"
              label={t('surveyCreation.timeline.planning.time')}
              error={errors?.time}
            >
              {(control) => (
                <select
                  {...control}
                  className="ds-input"
                  name="time"
                  value={time}
                  onChange={(e) => handleChange('time', e.target.value)}
                >
                  <option value="">{t('surveyCreation.timeline.planning.selectTime')}</option>
                  {withCurrent(TIME_SLOTS, time || undefined).map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              )}
            </WizardField>
          </>
        )}

        <WizardField
          id="survey-deadline"
          label={t('surveyCreation.timeline.deadline')}
          description={t('surveyCreation.timeline.deadlineDescription')}
          error={errors?.deadlineInDays}
        >
          {numberSelect('deadlineInDays', deadline, DEADLINE_OPTIONS, days)}
        </WizardField>

        <WizardField
          id="survey-reminder"
          label={t('surveyCreation.timeline.reminder')}
          description={t('surveyCreation.timeline.reminderDescription')}
          error={errors?.daysBeforeDeadlineToNotify}
        >
          {numberSelect(
            'daysBeforeDeadlineToNotify',
            reminder,
            [0, ...REMINDER_OPTIONS.filter((option) => option < deadline)],
            (count) =>
              count === 0
                ? t('surveyCreation.timeline.noReminder')
                : t('surveyCreation.timeline.reminderOption', { count }),
          )}
        </WizardField>

        {showActionDeadlines && (
          <>
            <WizardField
              id="survey-action-proposal-deadline"
              label={t('surveyCreation.timeline.actionProposalDeadline')}
              description={t('surveyCreation.timeline.actionProposalDescription')}
              error={errors?.actionProposalDeadlineInDays}
            >
              {numberSelect(
                'actionProposalDeadlineInDays',
                formData.actionProposalDeadlineInDays,
                DEADLINE_OPTIONS,
                days,
              )}
            </WizardField>
            <WizardField
              id="survey-action-voting-deadline"
              label={t('surveyCreation.timeline.actionVotingDeadline')}
              description={t('surveyCreation.timeline.actionVotingDescription')}
              error={errors?.actionVotingDeadlineInDays}
            >
              {numberSelect(
                'actionVotingDeadlineInDays',
                formData.actionVotingDeadlineInDays,
                DEADLINE_OPTIONS,
                days,
              )}
            </WizardField>
          </>
        )}
      </div>

      {isPlanning && (
        <fieldset
          className={`ds-fieldset ${classes.fieldset}`}
          aria-describedby={errors?.frequency ? 'survey-frequency-error' : undefined}
        >
          <legend className="ds-label">
            {t('surveyCreation.timeline.planning.repeatQuestion')}
          </legend>
          <div className={classes.chips}>
            {repeatOptions.map((option, index) => (
              <label className="ds-chip" key={option.value || 'none'}>
                <input
                  type="radio"
                  name="repeat"
                  id={index === 1 ? 'survey-frequency' : undefined}
                  checked={selectedRepeat === option.value}
                  onChange={() => {
                    handleChange('isRepeating', option.value !== '');
                    handleChange('frequency', option.value);
                  }}
                />
                {option.label}
              </label>
            ))}
          </div>
          {errors?.frequency && (
            <p
              className="ds-validation-message"
              data-color="danger"
              id="survey-frequency-error"
            >
              {errors.frequency}
            </p>
          )}
        </fieldset>
      )}

      {timelineItems.length > 0 && (
        <figure className={classes.preview}>
          <figcaption className="ds-label" data-size="sm">
            {t('surveyCreation.timeline.preview.title')}
          </figcaption>
          <ol className={classes.previewList}>
            {timelineItems.map((item) => (
              <li key={item.key} className={classes.previewItem}>
                <span aria-hidden="true" className={classes.previewIcon}>
                  {item.icon}
                </span>
                <span className={classes.previewText}>
                  <span className={`ds-paragraph ${classes.previewWhat}`} data-size="xs">
                    {item.what}
                  </span>
                  <span className="ds-label" data-size="sm">
                    {item.when}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </figure>
      )}
    </div>
  );
};
